import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
} from "@mui/material";
import {
  BeachAccess as VacationIcon,
  Cake as CakeIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  EventBusy as ProjectEndIcon,
} from "@mui/icons-material";
import { useHolidays } from "../hooks/useHolidays";
import { useUpcomingVacations } from "../hooks/useUpcomingVacations";
import { useUpcomingBirthdays } from "../hooks/useUpcomingBirthdays";
import { useUpcomingAnniversaries } from "../hooks/useUpcomingAnniversaries";
import { useUpcomingProjectEnds } from "../hooks/useUpcomingProjectEnds";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../constants";

const UpcomingEvents: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMIN;

  // Fetch all upcoming events
  const { data: upcomingVacations = [], isLoading: vacationsLoading } =
    useUpcomingVacations();
  const { holidays, isLoading: holidaysLoading } = useHolidays();
  const { upcomingBirthdays, isLoading: birthdaysLoading } =
    useUpcomingBirthdays(30, isAdmin);
  const { upcomingAnniversaries, isLoading: anniversariesLoading } =
    useUpcomingAnniversaries(30, isAdmin);
  const { upcomingProjectEnds, isLoading: projectEndsLoading } =
    useUpcomingProjectEnds(30, isAdmin);

  // Calculate upcoming holidays (next 30 days)
  const upcomingHolidays = useMemo(() => {
    if (!holidays || holidays.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 30);

    return holidays
      .filter((holiday: any) => {
        if (!holiday.isActive) return false;
        const holidayStart = new Date(holiday.startDate);
        const holidayEnd = new Date(holiday.endDate);
        holidayStart.setHours(0, 0, 0, 0);
        holidayEnd.setHours(23, 59, 59, 999);

        // Check if holiday overlaps with the next 30 days
        return holidayStart <= endDate && holidayEnd >= today;
      })
      .sort(
        (a: any, b: any) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      )
      .slice(0, 10); // Limit to 10 holidays
  }, [holidays]);

  const formatVacationDate = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const formatOptions: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
    };

    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString("en-US", formatOptions);
    }

    return `${start.toLocaleDateString("en-US", formatOptions)} - ${end.toLocaleDateString("en-US", formatOptions)}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontWeight: "bold", mb: 4 }}
      >
        Upcoming Events
      </Typography>

      {/* Upcoming Vacations Section */}
      <Card sx={{ boxShadow: 6, mb: 4 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <VacationIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upcoming Vacations:{" "}
              {!vacationsLoading &&
                upcomingVacations.length > 0 &&
                upcomingVacations.length}
            </Typography>
          </Box>

          {vacationsLoading ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : upcomingVacations.length === 0 ? (
            <Typography color="text.secondary">
              No upcoming vacations in the next 2 weeks.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2,
              }}
            >
              {upcomingVacations.map((vacation: any) => (
                <Box
                  key={vacation.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                    p: 2,
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    borderRadius: 2,
                    bgcolor: "rgba(33, 150, 243, 0.05)",
                    textAlign: "center",
                    minHeight: 120,
                    justifyContent: "center",
                  }}
                >
                  <Avatar
                    src={
                      vacation.employee?.user?.profilePicture ||
                      vacation.employee?.profilePicture
                    }
                    sx={{ bgcolor: "#2196f3", width: 48, height: 48 }}
                  >
                    {vacation.employee?.firstName?.charAt(0)}
                    {vacation.employee?.lastName?.charAt(0)}
                  </Avatar>
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {vacation.employee?.firstName}{" "}
                      {vacation.employee?.lastName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 1 }}
                    >
                      {formatVacationDate(vacation.startDate, vacation.endDate)}
                    </Typography>
                    <Chip
                      label="Vacation"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Holidays Section */}
      <Card sx={{ boxShadow: 6, mb: 4 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CalendarIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upcoming Holidays:{" "}
              {!holidaysLoading &&
                upcomingHolidays.length > 0 &&
                upcomingHolidays.length}
            </Typography>
          </Box>

          {holidaysLoading ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : upcomingHolidays.length === 0 ? (
            <Typography color="text.secondary">
              No upcoming holidays in the next 30 days.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2,
              }}
            >
              {upcomingHolidays.map((holiday: any) => {
                const startDate = new Date(holiday.startDate);
                const endDate = new Date(holiday.endDate);
                const formatOptions: Intl.DateTimeFormatOptions = {
                  month: "short",
                  day: "numeric",
                };
                const dateStr =
                  startDate.toDateString() === endDate.toDateString()
                    ? startDate.toLocaleDateString("en-US", formatOptions)
                    : `${startDate.toLocaleDateString("en-US", formatOptions)} - ${endDate.toLocaleDateString("en-US", formatOptions)}`;

                return (
                  <Box
                    key={holiday.id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.5,
                      p: 2,
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                      borderRadius: 2,
                      bgcolor: "rgba(156, 39, 176, 0.05)",
                      textAlign: "center",
                      minHeight: 120,
                      justifyContent: "center",
                    }}
                  >
                    <CalendarIcon sx={{ fontSize: 40, color: "#9c27b0" }} />
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {holiday.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        {dateStr}
                      </Typography>
                      {holiday.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", fontStyle: "italic" }}
                        >
                          {holiday.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Birthdays Section */}
      {isAdmin && (
        <Card sx={{ boxShadow: 6, mb: 4 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <CakeIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Upcoming Birthdays:{" "}
                {!birthdaysLoading &&
                  upcomingBirthdays.length > 0 &&
                  upcomingBirthdays.length}
              </Typography>
            </Box>

            {birthdaysLoading ? (
              <Typography color="text.secondary">Loading...</Typography>
            ) : upcomingBirthdays.length === 0 ? (
              <Typography color="text.secondary">
                No upcoming birthdays in the next 30 days.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {upcomingBirthdays.map((birthday: any) => {
                  const formatOptions: Intl.DateTimeFormatOptions = {
                    month: "short",
                    day: "numeric",
                  };
                  const dateStr = birthday.date.toLocaleDateString(
                    "en-US",
                    formatOptions,
                  );

                  return (
                    <Box
                      key={birthday.employee.id}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                        p: 2,
                        border: "1px solid rgba(255, 255, 255, 0.12)",
                        borderRadius: 2,
                        bgcolor: "rgba(255, 152, 0, 0.05)",
                        textAlign: "center",
                        minHeight: 120,
                        justifyContent: "center",
                      }}
                    >
                      <Avatar
                        src={
                          birthday.employee.user?.profilePicture ||
                          birthday.employee.profilePicture
                        }
                        sx={{ bgcolor: "#ff9800", width: 48, height: 48 }}
                      >
                        {birthday.employee.firstName?.charAt(0)}
                        {birthday.employee.lastName?.charAt(0)}
                      </Avatar>
                      <Box sx={{ width: "100%" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {birthday.employee.firstName}{" "}
                          {birthday.employee.lastName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          {dateStr}
                        </Typography>
                        <Chip
                          label={
                            birthday.daysUntil === 0
                              ? "Today!"
                              : `${birthday.daysUntil} days`
                          }
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem" }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Work Anniversaries Section */}
      {isAdmin && (
        <Card sx={{ boxShadow: 6, mb: 4 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <WorkIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Work Anniversaries:{" "}
                {!anniversariesLoading &&
                  upcomingAnniversaries.length > 0 &&
                  upcomingAnniversaries.length}
              </Typography>
            </Box>

            {anniversariesLoading ? (
              <Typography color="text.secondary">Loading...</Typography>
            ) : upcomingAnniversaries.length === 0 ? (
              <Typography color="text.secondary">
                No upcoming work anniversaries in the next 30 days.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {upcomingAnniversaries.map((anniversary: any) => {
                  const formatOptions: Intl.DateTimeFormatOptions = {
                    month: "short",
                    day: "numeric",
                  };
                  const dateStr = anniversary.date.toLocaleDateString(
                    "en-US",
                    formatOptions,
                  );

                  return (
                    <Box
                      key={anniversary.employee.id}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                        p: 2,
                        border: "1px solid rgba(255, 255, 255, 0.12)",
                        borderRadius: 2,
                        bgcolor: "rgba(46, 125, 50, 0.05)",
                        textAlign: "center",
                        minHeight: 120,
                        justifyContent: "center",
                      }}
                    >
                      <Avatar
                        src={
                          anniversary.employee.user?.profilePicture ||
                          anniversary.employee.profilePicture
                        }
                        sx={{ bgcolor: "#2e7d32", width: 48, height: 48 }}
                      >
                        {anniversary.employee.firstName?.charAt(0)}
                        {anniversary.employee.lastName?.charAt(0)}
                      </Avatar>
                      <Box sx={{ width: "100%" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {anniversary.employee.firstName}{" "}
                          {anniversary.employee.lastName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          {dateStr}
                        </Typography>
                        <Chip
                          label={
                            anniversary.daysUntil === 0
                              ? `${anniversary.years} years today!`
                              : `${anniversary.years} years in ${anniversary.daysUntil} days`
                          }
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem" }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upcoming Project Ends Section */}
      {isAdmin && (
        <Card sx={{ boxShadow: 6, mb: 4 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <ProjectEndIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Upcoming Project Ends:{" "}
                {!projectEndsLoading &&
                  upcomingProjectEnds.length > 0 &&
                  upcomingProjectEnds.length}
              </Typography>
            </Box>

            {projectEndsLoading ? (
              <Typography color="text.secondary">Loading...</Typography>
            ) : upcomingProjectEnds.length === 0 ? (
              <Typography color="text.secondary">
                No upcoming project ends in the next 30 days.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {upcomingProjectEnds.map((projectEnd: any, index: number) => {
                  const formatOptions: Intl.DateTimeFormatOptions = {
                    month: "short",
                    day: "numeric",
                  };
                  const dateStr = projectEnd.endDate.toLocaleDateString(
                    "en-US",
                    formatOptions,
                  );

                  return (
                    <Box
                      key={`${projectEnd.project.id}-${projectEnd.employee.id}-${index}`}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                        p: 2,
                        border: "1px solid rgba(255, 255, 255, 0.12)",
                        borderRadius: 2,
                        bgcolor: "rgba(244, 67, 54, 0.05)",
                        textAlign: "center",
                        minHeight: 120,
                        justifyContent: "center",
                      }}
                    >
                      <Avatar
                        src={
                          projectEnd.employee?.user?.profilePicture ||
                          projectEnd.employee?.profilePicture
                        }
                        sx={{ bgcolor: "#f44336", width: 48, height: 48 }}
                      >
                        {projectEnd.employee.firstName?.charAt(0)}
                        {projectEnd.employee.lastName?.charAt(0)}
                      </Avatar>
                      <Box sx={{ width: "100%" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {projectEnd.employee.firstName}{" "}
                          {projectEnd.employee.lastName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 0.5 }}
                        >
                          {projectEnd.project.name}
                        </Typography>
                        {projectEnd.project.client?.name && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mb: 0.5,
                              fontStyle: "italic",
                            }}
                          >
                            {projectEnd.project.client.name}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          {dateStr}
                        </Typography>
                        <Chip
                          label={
                            projectEnd.daysUntil === 0
                              ? "Ends today!"
                              : `${projectEnd.daysUntil} days`
                          }
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem" }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default UpcomingEvents;
